import { useEffect, useState } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import { isBillingConfigured } from '../env';
import { openCheckout, openPaymentLink } from '../lib/paddle';
import { Button } from '../components/ui';
import { AuthLayout } from './auth/auth-layout';

/**
 * Paddle's payment link: `/pay?_ptxn=txn_...`.
 *
 * Paddle sends customers here to complete a transaction -- most importantly from the email asking a
 * customer whose renewal failed to pay again. It is the page configured as the default payment link
 * in Paddle's checkout settings, and all it has to do is put that transaction's checkout in front
 * of the customer.
 *
 * Public, deliberately. The customer arrives from an email and may not be signed in; sending them
 * through login first would drop `?_ptxn=` on the way and leave them nowhere. Nothing here needs a
 * session: the transaction already names the customer and the price, the page only opens Paddle's
 * checkout for it, and whether it was paid is decided by Paddle's webhook, not by this page.
 */

/** Paddle transaction IDs: `txn_` and 26 lowercase base32 characters. */
const TRANSACTION_ID = /^txn_[a-z0-9]{26}$/;

type Stage = 'opening' | 'open' | 'closed' | 'completed' | 'failed';

export function PayRoute() {
  const { _ptxn: transactionId } = useSearch({ from: '/pay' });
  const valid = transactionId !== undefined && TRANSACTION_ID.test(transactionId);
  const [stage, setStage] = useState<Stage>('opening');

  useEffect(() => {
    if (!valid || !isBillingConfigured) return;
    let active = true;
    openPaymentLink({
      transactionId,
      completed: () => {
        if (active) setStage('completed');
      },
      closed: () => {
        if (active) setStage((current) => (current === 'completed' ? current : 'closed'));
      },
    })
      .then(() => {
        if (active) setStage((current) => (current === 'opening' ? 'open' : current));
      })
      .catch(() => {
        if (active) setStage('failed');
      });
    return () => {
      active = false;
    };
  }, [transactionId, valid]);

  if (!valid) {
    return (
      <AuthLayout title="Payment link incomplete" footer={<BillingLink />}>
        <p className="text-sm text-neutral-700" data-testid="pay-invalid">
          This link does not name a payment. Open it again from the email Paddle sent you, or manage
          your plan from the Billing page.
        </p>
      </AuthLayout>
    );
  }

  if (!isBillingConfigured) {
    return (
      <AuthLayout title="Payments unavailable" footer={<BillingLink />}>
        <p className="text-sm text-neutral-700">Payments are not configured in this environment.</p>
      </AuthLayout>
    );
  }

  const reopen = () => {
    setStage('opening');
    openCheckout({
      transactionId,
      email: undefined,
      completed: () => setStage('completed'),
      closed: () => setStage((current) => (current === 'completed' ? current : 'closed')),
    })
      .then(() => setStage((current) => (current === 'opening' ? 'open' : current)))
      .catch(() => setStage('failed'));
  };

  return (
    <AuthLayout
      title={stage === 'completed' ? 'Payment received' : 'Complete your payment'}
      footer={<BillingLink />}
    >
      {(stage === 'opening' || stage === 'open') && (
        <p className="text-sm text-neutral-600" data-testid="pay-opening">
          Opening Paddle&rsquo;s secure checkout. Paddle is our payment provider and handles your
          card details; they never reach urlshot.io.
        </p>
      )}
      {stage === 'completed' && (
        <p className="text-sm text-neutral-700" data-testid="pay-completed">
          Thank you. Paddle is confirming the payment, and your plan on the Billing page updates as
          soon as it has.
        </p>
      )}
      {stage === 'closed' && (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-neutral-700">The checkout was closed before payment.</p>
          <Button onClick={reopen}>Open checkout again</Button>
        </div>
      )}
      {stage === 'failed' && (
        <div className="flex flex-col gap-3">
          <p role="alert" className="text-sm text-red-800">
            Paddle&rsquo;s checkout could not be loaded. A browser extension may be blocking it.
          </p>
          <Button onClick={reopen}>Try again</Button>
        </div>
      )}
    </AuthLayout>
  );
}

function BillingLink() {
  return (
    <Link to="/billing" className="link">
      Go to Billing
    </Link>
  );
}
